import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { VbsCalculationPreview } from "@/services/backend/documents/components/preview/vbs/vbs-calculation-preview";
import { VbsCertificatePreview } from "@/services/backend/documents/components/preview/vbs/vbs-certificate-preview";
import { VbsConfirmationPreview } from "@/services/backend/documents/components/preview/vbs/vbs-confirmation-preview";
import { VbsOfferPreview } from "@/services/backend/documents/components/preview/vbs/vbs-offer-preview";
import { VbsOrderPreview } from "@/services/backend/documents/components/preview/vbs/vbs-order-preview";
import { VbsWorkOrderPreview } from "@/services/backend/documents/components/preview/vbs/vbs-work-order-preview";
import { RltCalculationPreview } from "@/services/backend/documents/components/preview/rlt/rlt-calculation-preview";
import { RltCertificatePreview } from "@/services/backend/documents/components/preview/rlt/rlt-certificate-preview";
import { RltConfirmationPreview } from "@/services/backend/documents/components/preview/rlt/rlt-confirmation-preview";
import { RltOfferPreview } from "@/services/backend/documents/components/preview/rlt/rlt-offer-preview";
import { RltOrderPreview } from "@/services/backend/documents/components/preview/rlt/rlt-order-preview";
import { RltWorkOrderPreview } from "@/services/backend/documents/components/preview/rlt/rlt-work-order-preview";
import { DocumentKind } from "@bitory-gmbh/bsm";
import { NavigateToDocumentProvider } from "@/services/backend/documents/components/preview/_components/navigate-to-document-context";

export interface DocumentPreviewProps {
  documentId: string;
  documentKind: DocumentKind;
  serviceKind: GSMServiceKind;
  onNavigateToDocument?: (path: string) => void;
}

export function DocumentPreview(props: DocumentPreviewProps) {
  const { documentId, documentKind, serviceKind, onNavigateToDocument } = props;
  return (
    <NavigateToDocumentProvider onNavigate={onNavigateToDocument}>
      <DocumentPreviewInner
        documentId={documentId}
        documentKind={documentKind}
        serviceKind={serviceKind}
      />
    </NavigateToDocumentProvider>
  );
}

function DocumentPreviewInner({
  documentId,
  documentKind,
  serviceKind,
}: {
  documentId: string;
  documentKind: DocumentKind;
  serviceKind: GSMServiceKind;
}) {
  switch (serviceKind) {
    case GSMServiceKind.VBS:
      return (
        <VbsDocumentPreview
          documentId={documentId}
          documentKind={documentKind}
        />
      );
    case GSMServiceKind.RLT:
      return (
        <RltDocumentPreview
          documentId={documentId}
          documentKind={documentKind}
        />
      );
    default:
      // Just show nothing if not implemented.
      return null;
  }
}

function VbsDocumentPreview({
  documentId,
  documentKind,
}: {
  documentId: string;
  documentKind: DocumentKind;
}) {
  switch (documentKind) {
    case GSMDocumentKind.Calculation:
      return <VbsCalculationPreview calculationId={documentId} />;
    case GSMDocumentKind.Certificate:
      return <VbsCertificatePreview certificateId={documentId} />;
    case GSMDocumentKind.Confirmation:
      return <VbsConfirmationPreview confirmationId={documentId} />;
    case GSMDocumentKind.Contract:
      // As of 25.07.2024 it was clear that contracts will change significantly
      // form the modelling to date. Therefore, previews where not implemented.
      break;
    case GSMDocumentKind.Documentation:
      // can be found via the work order
      break;
    case GSMDocumentKind.LastMinuteRiskAnalysis:
      // can be found via the work order
      break;
    case GSMDocumentKind.Offer:
      return <VbsOfferPreview offerId={documentId} />;
    case GSMDocumentKind.Order:
      return <VbsOrderPreview orderId={documentId} />;
    case GSMDocumentKind.Requirement:
      // does not apply to VBS
      return null;
    case GSMDocumentKind.Treatment:
      // It makes no sense to search for since a VBS treatment is always
      // coupled to another document / not standing on its own from
      // a user perspektive.
      break;
    case GSMDocumentKind.WorkOrder:
      return <VbsWorkOrderPreview workOrderId={documentId} />;
    default:
      // Just show nothing if not implemented.
      return null;
  }
}

function RltDocumentPreview({
  documentId,
  documentKind,
}: {
  documentId: string;
  documentKind: DocumentKind;
}) {
  switch (documentKind) {
    case GSMDocumentKind.Calculation:
      return <RltCalculationPreview calculationId={documentId} />;
    case GSMDocumentKind.Certificate:
      return <RltCertificatePreview certificateId={documentId} />;
    case GSMDocumentKind.Confirmation:
      return <RltConfirmationPreview confirmationId={documentId} />;
    case GSMDocumentKind.Contract:
      // As of 25.07.2024 it was clear that contracts will change significantly
      // form the modelling to date. Therefore, previews where not implemented.
      break;
    case GSMDocumentKind.Documentation:
      // can be found via the work order
      break;
    case GSMDocumentKind.LastMinuteRiskAnalysis:
      // can be found via the work order
      break;
    case GSMDocumentKind.Offer:
      return <RltOfferPreview offerId={documentId} />;
    case GSMDocumentKind.Order:
      return <RltOrderPreview orderId={documentId} />;
    case GSMDocumentKind.Requirement:
      // does not apply to RLT
      return null;
    case GSMDocumentKind.Treatment:
      // It makes no sense to search for since a VBS treatment is always
      // coupled to another document / not standing on its own from
      // a user perspektive.
      break;
    case GSMDocumentKind.WorkOrder:
      return <RltWorkOrderPreview workOrderId={documentId} />;
    default:
      // Just show nothing if not implemented.
      return null;
  }
}
